import { Injectable } from '@angular/core';
import { InitialContactDialogData } from './initial-contact-dialog/initial-contact-dialog.model';
import { Observable, from, combineLatest } from 'rxjs';
import { Contact } from './contact.model';
import {
  AngularFirestore,
  DocumentReference,
  DocumentChangeAction
} from '@angular/fire/firestore';
import { ContactMessage } from './contact-message.model';
import { switchMap, map } from 'rxjs/operators';
import { ContactPreview } from './contact-preview.model';
import { firestore } from 'firebase';

@Injectable({
  providedIn: 'root'
})
export class ContactService {
  constructor(private firestoreDb: AngularFirestore) {}

  initialiseContact(
    initialiseContact: InitialContactDialogData
  ): Observable<DocumentReference> {
    const contact = new Contact({
      serviceKey: initialiseContact.serviceDocumentKey,
      clientUserKey: initialiseContact.clientUserKey
    });
    return from(
      this.firestoreDb
        .doc<Contact>(
          `services/${initialiseContact.serviceDocumentKey}/contacts/${initialiseContact.clientUserKey}`
        )
        .set({ ...contact })
    ).pipe(
      switchMap(() =>
        this.addMessageToContact(
          initialiseContact.serviceDocumentKey,
          initialiseContact.clientUserKey,
          initialiseContact.clientUserKey,
          initialiseContact.message,
          initialiseContact.timeStamp
        )
      )
    );
  }

  getContactByKey(
    serviceKey: string,
    clientUserKey: string
  ): Observable<Contact> {
    return this.firestoreDb
      .doc<Contact>(`services/${serviceKey}/contacts/${clientUserKey}`)
      .valueChanges();
  }

  getContactByClient(userKey: string): Observable<Array<Contact>> {
    return this.firestoreDb
      .collectionGroup<Contact>('contacts', ref =>
        ref.where('clientUserKey', '==', userKey)
      )
      .snapshotChanges()
      .pipe(
        map(doc => {
          const contacts = [];
          for (const contact of doc) {
            contacts.push(
              new Contact({
                contactKey: contact.payload.doc.id,
                clientUserKey: contact.payload.doc.data().clientUserKey,
                serviceKey: contact.payload.doc.data().serviceKey
              })
            );
          }
          return contacts;
        })
      );
  }

  getContactForServiceProvider(serviceKey: string): Observable<Array<Contact>> {
    return this.firestoreDb
      .collectionGroup<Contact>('contacts', ref =>
        ref.where('serviceKey', '==', serviceKey)
      )
      .snapshotChanges()
      .pipe(
        map(doc => {
          const contacts = [];
          for (const contact of doc) {
            contacts.push(
              new Contact({
                contactKey: contact.payload.doc.id,
                clientUserKey: contact.payload.doc.data().clientUserKey,
                serviceKey: contact.payload.doc.data().serviceKey
              })
            );
          }
          return contacts;
        })
      );
  }

  getLatestMessageForContacts(
    contacts: DocumentChangeAction<Contact>[]
  ): Observable<any> {
    const queries = contacts.map(contact =>
      from(
        contact.payload.doc.ref
          .collection('messages')
          .orderBy('timeStamp', 'desc')
          .limit(1)
          .get()
      ).pipe(map(messages => messages[0]))
    );

    return combineLatest(queries);
  }

  getLatestContactMessage(
    serviceKey: string,
    clientUserKey: string
  ): Observable<ContactMessage> {
    return this.firestoreDb
      .collection<ContactMessage>(
        `services/${serviceKey}/contacts/${clientUserKey}/messages`,
        ref => ref.orderBy('timeStamp', 'desc').limit(1)
      )
      .valueChanges()
      .pipe(map(messages => messages[0]));
  }

  getContactMessages(
    serviceKey: string,
    clientUserKey: string
  ): Observable<Array<ContactMessage>> {
    return this.firestoreDb
      .collection<ContactMessage>(
        `services/${serviceKey}/contacts/${clientUserKey}/messages`,
        ref => ref.orderBy('timeStamp', 'asc')
      )
      .valueChanges();
  }

  addMessageToContact(
    serviceKey: string,
    clientKey: string,
    senderKey: string,
    message: string,
    timeStamp: firestore.Timestamp
  ): Observable<DocumentReference> {
    return from(
      this.firestoreDb
        .collection<ContactMessage>(
          `services/${serviceKey}/contacts/${clientKey}/messages`
        )
        .add({ message, timeStamp, senderKey })
    );
  }

  private getContactPreviews(
    contacts: Contact[]
  ): Observable<Array<ContactPreview>> {
    // return this.availableServicesService.getServiceProviderPreview(contacts);

    return from([]);
  }
}
